import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { User } from '../_models/user';

@Injectable()
export class AuthGuard {

  constructor(
    private auth: AuthService,
    private router: Router,
    private firestore: Firestore
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> {

    const schoolId = next.paramMap.get('schoolId') ?? '';

    return this.auth.user.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          return of(this.router.createUrlTree(['/epl-login']));
        }

        const nextPathName = next.routeConfig?.path || '';

        if (nextPathName === 'schools/new' || nextPathName === 'defaults/content') {
          return of(user.admin ? true : this.auth.getHomeUrlTree(user));
        }

        if (user.admin) {
          return schoolId ? from(this.confirmHqSchool(schoolId)) : of(true);
        }

        return of(this.schoolUserDecision(user, nextPathName, schoolId));
      })
    );
  }

  /** Headquarters admins can open any real school. An unknown id returns to the school list. */
  private async confirmHqSchool(schoolId: string): Promise<boolean | UrlTree> {
    try {
      const snap = await getDoc(doc(this.firestore, 'schools', schoolId));
      return snap.exists() ? true : this.router.createUrlTree(['/schools']);
    } catch (error) {
      console.error('Could not confirm school ' + schoolId, error);
      return true;
    }
  }

  private schoolUserDecision(user: User, nextPathName: string, schoolId: string): boolean | UrlTree {
    if (!user.schools?.length) {
      return this.router.createUrlTree(['/no-access']);
    }

    if (user.schools.length > 1 && nextPathName === 'schools') {
      return true;
    }

    if (user.schools.indexOf(schoolId) !== -1) {
      if (nextPathName.includes('admin')) {
        return this.auth.getHomeUrlTree(user);
      }
      return true;
    }

    return this.auth.getHomeUrlTree(user);
  }
}
